import { z } from "zod";
import type { ActionType } from "./types";

export type SopEnforcement = "block" | "warn" | "inform" | "relax";
export type SopDayOfWeek = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type SopPredicateDsl =
  | { op: "always" }
  | { op: "exists"; path: string }
  | { op: "equals"; path: string; value: unknown }
  | { op: "not_equals"; path: string; value: unknown }
  | { op: "lt" | "lte" | "gt" | "gte"; path: string; value: number }
  | { op: "includes"; path: string; value: unknown }
  | { op: "array_includes"; path: string; value: unknown }
  | { op: "string_contains"; path: string; value: string }
  | {
      op: "time_of_day_lt" | "time_of_day_gt";
      path: string;
      hour: number;
      minute?: number;
      timezone?: string;
    }
  | { op: "day_of_week_in"; path: string; days: SopDayOfWeek[]; timezone?: string }
  | { op: "date_lt" | "date_gt" | "date_between"; path: string; iso: string; isoEnd?: string }
  | { op: "and" | "or"; predicates: SopPredicateDsl[] }
  | { op: "not"; predicate: SopPredicateDsl };

export interface CustomerSopRecord {
  _id?: string;
  tenantId: string;
  customerId: string;
  scope: "global" | "lane" | "commodity" | "facility";
  scopeKey?: string;
  category:
    | "equipment_rules"
    | "driver_rules"
    | "appointment_rules"
    | "paperwork_rules"
    | "billing_rules"
    | "communication_rules"
    | "detention_rules"
    | "tracking_rules"
    | "safety_rules"
    | "blind_rules";
  rule: string;
  enforcement: SopEnforcement;
  preAuthorizedAmountCents?: number;
  compiledPredicate?: SopPredicateDsl;
}

export interface SopEvaluationContext {
  tenantId: string;
  customerId: string;
  actionType?: ActionType;
  laneKey?: string;
  commodityCategory?: string;
  facilityIds?: string[];
  amountCents?: number;
  evidenceDocIds?: string[];
  load?: Record<string, unknown>;
  carrier?: Record<string, unknown>;
  driver?: Record<string, unknown>;
  payload?: Record<string, unknown>;
  currentTime?: { iso: string; timezone: string };
}

export interface SopEvaluationHit {
  sopId?: string;
  category: CustomerSopRecord["category"];
  rule: string;
  enforcement: SopEnforcement;
  reason: string;
  preAuthorizedAmountCents?: number;
}

export interface SopEvaluationResult {
  blocks: SopEvaluationHit[];
  warnings: SopEvaluationHit[];
  informs: SopEvaluationHit[];
  relaxations: SopEvaluationHit[];
}

type CompiledPredicate = (context: SopEvaluationContext) => boolean;

const tenantPredicateCache = new Map<string, Map<string, CompiledPredicate>>();
let deprecatedIncludesWarned = false;

export function invalidateCustomerSopCache(tenantId: string): void {
  tenantPredicateCache.delete(tenantId);
}

export function compileSopPredicate(dsl: SopPredicateDsl | undefined): CompiledPredicate {
  if (!dsl) return () => true;

  switch (dsl.op) {
    case "always":
      return () => true;
    case "exists":
      return (context) => readPath(context, dsl.path) !== undefined;
    case "equals":
      return (context) => readPath(context, dsl.path) === dsl.value;
    case "not_equals":
      return (context) => readPath(context, dsl.path) !== dsl.value;
    case "lt":
      return (context) => Number(readPath(context, dsl.path)) < dsl.value;
    case "lte":
      return (context) => Number(readPath(context, dsl.path)) <= dsl.value;
    case "gt":
      return (context) => Number(readPath(context, dsl.path)) > dsl.value;
    case "gte":
      return (context) => Number(readPath(context, dsl.path)) >= dsl.value;
    case "includes":
      return (context) => {
        warnDeprecatedIncludes();
        return evaluateIncludesAlias(readPath(context, dsl.path), dsl.value);
      };
    case "array_includes":
      return (context) => {
        const value = readPath(context, dsl.path);
        return Array.isArray(value) && value.includes(dsl.value);
      };
    case "string_contains":
      return (context) => {
        const value = readPath(context, dsl.path);
        return String(value ?? "").toLowerCase().includes(dsl.value.toLowerCase());
      };
    case "time_of_day_lt":
      return (context) =>
        minutesSinceMidnight(resolveIsoForPath(context, dsl.path), timezoneFor(context, dsl.timezone)) <
        dsl.hour * 60 + (dsl.minute ?? 0);
    case "time_of_day_gt":
      return (context) =>
        minutesSinceMidnight(resolveIsoForPath(context, dsl.path), timezoneFor(context, dsl.timezone)) >
        dsl.hour * 60 + (dsl.minute ?? 0);
    case "day_of_week_in":
      return (context) =>
        dsl.days.includes(dayOfWeek(resolveIsoForPath(context, dsl.path), timezoneFor(context, dsl.timezone)));
    case "date_lt":
      return (context) => Date.parse(resolveIsoForPath(context, dsl.path)) < Date.parse(dsl.iso);
    case "date_gt":
      return (context) => Date.parse(resolveIsoForPath(context, dsl.path)) > Date.parse(dsl.iso);
    case "date_between":
      return (context) => {
        const date = Date.parse(resolveIsoForPath(context, dsl.path));
        return date >= Date.parse(dsl.iso) && date <= Date.parse(dsl.isoEnd ?? dsl.iso);
      };
    case "and": {
      const predicates = dsl.predicates.map(compileSopPredicate);
      return (context) => predicates.every((predicate) => predicate(context));
    }
    case "or": {
      const predicates = dsl.predicates.map(compileSopPredicate);
      return (context) => predicates.some((predicate) => predicate(context));
    }
    case "not": {
      const predicate = compileSopPredicate(dsl.predicate);
      return (context) => !predicate(context);
    }
  }
}

export function evaluateCustomerSops(
  sops: CustomerSopRecord[],
  context: SopEvaluationContext,
): SopEvaluationResult {
  const result: SopEvaluationResult = {
    blocks: [],
    warnings: [],
    informs: [],
    relaxations: [],
  };

  for (const sop of sops) {
    if (!isApplicableSop(sop, context)) continue;
    const predicate = getCachedPredicate(sop);
    if (!predicate(context)) continue;

    const hit: SopEvaluationHit = {
      sopId: sop._id,
      category: sop.category,
      rule: sop.rule,
      enforcement: sop.enforcement,
      reason: sop.rule,
      preAuthorizedAmountCents: sop.preAuthorizedAmountCents,
    };

    if (sop.enforcement === "block") result.blocks.push(hit);
    if (sop.enforcement === "warn") result.warnings.push(hit);
    if (sop.enforcement === "inform") result.informs.push(hit);
    if (sop.enforcement === "relax") result.relaxations.push(hit);
  }

  return result;
}

export function isApplicableSop(sop: CustomerSopRecord, context: SopEvaluationContext): boolean {
  if (sop.tenantId !== context.tenantId || sop.customerId !== context.customerId) return false;
  if (sop.scope === "global") return true;
  if (sop.scope === "lane") return !!sop.scopeKey && sop.scopeKey === context.laneKey;
  if (sop.scope === "commodity") return !!sop.scopeKey && sop.scopeKey === context.commodityCategory;
  if (sop.scope === "facility") return !!sop.scopeKey && (context.facilityIds ?? []).includes(sop.scopeKey);
  return false;
}

function getCachedPredicate(sop: CustomerSopRecord): CompiledPredicate {
  const tenantCache = getTenantCache(sop.tenantId);
  const cacheKey = sop._id ?? `${sop.customerId}:${sop.category}:${sop.rule}`;
  const cached = tenantCache.get(cacheKey);
  if (cached) return cached;

  const compiled = compileSopPredicate(sop.compiledPredicate);
  tenantCache.set(cacheKey, compiled);
  return compiled;
}

function getTenantCache(tenantId: string): Map<string, CompiledPredicate> {
  const existing = tenantPredicateCache.get(tenantId);
  if (existing) return existing;
  const created = new Map<string, CompiledPredicate>();
  tenantPredicateCache.set(tenantId, created);
  return created;
}

function readPath(source: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, segment) => {
    if (current == null || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[segment];
  }, source);
}

function resolveIsoForPath(context: SopEvaluationContext, path: string): string {
  const value = readPath(context, path);
  if (typeof value === "string" && !Number.isNaN(Date.parse(value))) return value;
  return context.currentTime?.iso ?? new Date().toISOString();
}

function timezoneFor(context: SopEvaluationContext, override: string | undefined): string {
  return override ?? context.currentTime?.timezone ?? "UTC";
}

function minutesSinceMidnight(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? 0);
  return hour * 60 + minute;
}

function dayOfWeek(iso: string, timezone: string): SopDayOfWeek {
  const day = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
  })
    .format(new Date(iso))
    .toLowerCase()
    .slice(0, 3);
  return day as SopDayOfWeek;
}

function evaluateIncludesAlias(value: unknown, expected: unknown): boolean {
  if (Array.isArray(value)) return value.includes(expected);
  return String(value ?? "").toLowerCase().includes(String(expected).toLowerCase());
}

function warnDeprecatedIncludes(): void {
  if (deprecatedIncludesWarned || process.env.NODE_ENV === "production") return;
  deprecatedIncludesWarned = true;
  console.warn("SOP predicate op 'includes' is deprecated; use 'array_includes' or 'string_contains'.");
}

/** Parse persisted JSON before passing it into the executable SOP DSL. */
export function parseSopPredicate(value: unknown): SopPredicateDsl | undefined {
  if (value === undefined) return undefined;
  return storedSopPredicate.parse(value);
}

const storedSopPredicate: z.ZodType<SopPredicateDsl> = z.lazy(() => z.discriminatedUnion("op", [
  z.object({ op: z.literal("always") }),
  z.object({ op: z.literal("exists"), path: z.string() }),
  z.object({ op: z.enum(["equals", "not_equals", "includes", "array_includes"]), path: z.string(), value: z.unknown() }),
  z.object({ op: z.enum(["lt", "lte", "gt", "gte"]), path: z.string(), value: z.number() }),
  z.object({ op: z.literal("string_contains"), path: z.string(), value: z.string() }),
  z.object({ op: z.enum(["time_of_day_lt", "time_of_day_gt"]), path: z.string(), hour: z.number(), minute: z.number().optional(), timezone: z.string().optional() }),
  z.object({ op: z.literal("day_of_week_in"), path: z.string(), days: z.array(z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"])), timezone: z.string().optional() }),
  z.object({ op: z.enum(["date_lt", "date_gt", "date_between"]), path: z.string(), iso: z.string(), isoEnd: z.string().optional() }),
  z.object({ op: z.enum(["and", "or"]), predicates: z.array(storedSopPredicate) }),
  z.object({ op: z.literal("not"), predicate: storedSopPredicate }),
]));
